import type { SQLiteDatabase } from 'expo-sqlite';

/**
 * Achievement system — derived from existing DB data so there's no extra
 * persistence layer. Compute the list on demand; UI surfaces both "earned"
 * and "in-progress" so users see what they're chasing next.
 *
 * Achievements deliberately span low/medium/high difficulty:
 *  - Easy wins (first completion, first inventory item) create early
 *    dopamine for ADHD-tuned users.
 *  - Mid-tier (10 done, 25 done, all systems added) sustain the loop.
 *  - Aspirational (100 done, full house profile, every season touched)
 *    give long-term goals to chase.
 */

export interface Achievement {
  key: string;
  title: string;
  body: string;
  earned: boolean;
  /** 0–100. When earned, always 100. Otherwise current/target. */
  progress: number;
  /** Stage tier — used to sort and color-code the badge. */
  tier: 'bronze' | 'silver' | 'gold';
  /** Lucide icon name (resolved by the row renderer). Each achievement
   *  has a distinct icon so the list is visually scannable. If a custom
   *  PNG lives at `assets/images/achievements/{key}.png` the row renders
   *  that instead. */
  icon: string;
}

export interface AchievementStats {
  taskCompletions: number;
  taskSkips: number;
  inventoryCount: number;
  systemCount: number;
  expenseCount: number;
  documentCount: number;
  contactCount: number;
  profileFilled: number;
  profileTotal: number;
  goalsCompleted: number;
  seasonsTouched: number;
  /** Current consecutive-day completion streak. */
  streakDays: number;
  /** Highest per-season completion %, used for half-season / full-season badges. */
  bestSeasonPct: number;
  /** True when the user has uploaded a home background photo. */
  hasHomePhoto: boolean;
  /** Completions that carried a cost or vendor — "I really did it" signal. */
  detailedCompletions: number;
}

const PROFILE_FIELDS: string[] = [
  'name', 'address', 'zip', 'home_type', 'year_band', 'sqft',
  'bedrooms', 'bathrooms', 'hvac_type', 'lot_band', 'basement',
  'foundation', 'roof_material', 'exterior_material', 'garage',
  'water_source', 'sewer', 'background_uri',
];

export async function computeAchievementStats(
  db: SQLiteDatabase,
  propertyId: string
): Promise<AchievementStats> {
  const get = async (sql: string): Promise<number> => {
    const row = await db.getFirstAsync<{ n: number }>(sql, propertyId);
    return row?.n ?? 0;
  };

  const taskCompletions = await get(
    `SELECT COUNT(*) AS n FROM task t
     JOIN task_completion tc ON tc.task_id = t.id
     WHERE t.property_id = ? AND COALESCE(tc.kind, 'done') = 'done'`
  );
  const taskSkips = await get(
    `SELECT COUNT(*) AS n FROM task t
     JOIN task_completion tc ON tc.task_id = t.id
     WHERE t.property_id = ? AND tc.kind = 'skip'`
  );
  const inventoryCount = await get(
    `SELECT COUNT(*) AS n FROM inventory_item WHERE property_id = ? AND deleted_at IS NULL`
  );
  const systemCount = await get(
    `SELECT COUNT(*) AS n FROM system WHERE property_id = ? AND deleted_at IS NULL`
  );
  const expenseCount = await get(
    `SELECT COUNT(*) AS n FROM expense WHERE property_id = ? AND deleted_at IS NULL`
  );
  const documentCount = await get(
    `SELECT COUNT(*) AS n FROM document WHERE property_id = ? AND deleted_at IS NULL`
  );
  const contactCount = await get(
    `SELECT COUNT(*) AS n FROM contact WHERE property_id = ? AND deleted_at IS NULL`
  );
  const goalsCompleted = await get(
    `SELECT COUNT(*) AS n FROM goal WHERE property_id = ? AND status = 'done' AND deleted_at IS NULL`
  ).catch(() => 0);

  // Property profile completeness — count non-null fields.
  const prop = await db.getFirstAsync<Record<string, unknown>>(
    `SELECT * FROM property WHERE id = ?`,
    propertyId
  );
  let profileFilled = 0;
  for (const f of PROFILE_FIELDS) {
    const v = prop?.[f];
    if (v != null && v !== '' && v !== 0) profileFilled += 1;
  }

  // How many distinct seasons has the user completed at least one task in?
  const seasonsRow = await db.getFirstAsync<{ n: number }>(
    `SELECT COUNT(DISTINCT t.season) AS n FROM task t
     JOIN task_completion tc ON tc.task_id = t.id
     WHERE t.property_id = ? AND t.season IN ('spring','summer','fall','winter')
       AND COALESCE(tc.kind, 'done') = 'done'`,
    propertyId
  );
  const seasonsTouched = seasonsRow?.n ?? 0;

  // Walk back from today through completion days. Mirror of getStreakDays
  // in tasks.ts; kept inline here so the achievement compute doesn't drag
  // in an extra import cycle.
  const completionDays = await db.getAllAsync<{ ts: number }>(
    `SELECT tc.completed_at AS ts FROM task_completion tc
     JOIN task t ON t.id = tc.task_id
     WHERE t.property_id = ? AND t.deleted_at IS NULL
     ORDER BY tc.completed_at DESC LIMIT 1000`,
    propertyId
  );
  const dayKeys = new Set<string>();
  for (const r of completionDays) {
    const d = new Date(r.ts);
    dayKeys.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  }
  let streakDays = 0;
  if (dayKeys.size > 0) {
    const cursor = new Date();
    const todayKey = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
    if (!dayKeys.has(todayKey)) cursor.setDate(cursor.getDate() - 1);
    while (true) {
      const key = `${cursor.getFullYear()}-${cursor.getMonth()}-${cursor.getDate()}`;
      if (!dayKeys.has(key)) break;
      streakDays += 1;
      cursor.setDate(cursor.getDate() - 1);
    }
  }

  // Best-season completion % across all four — drives the "half-season"
  // and "season swept" badges. We share the same shared computation the
  // Home screen + Schedule chip row use (via getAllSeasonsProgress)
  // by-passing it here would risk drift; keep the SQL inline for now.
  const yr = new Date().getFullYear();
  const SEASON_BOUNDS: Record<'spring' | 'summer' | 'fall' | 'winter', [number, number]> = {
    spring: [new Date(yr, 2, 20).getTime(), new Date(yr, 5, 21).getTime()],
    summer: [new Date(yr, 5, 21).getTime(), new Date(yr, 8, 22).getTime()],
    fall:   [new Date(yr, 8, 22).getTime(), new Date(yr, 11, 21).getTime()],
    winter: [new Date(yr - 1, 11, 21).getTime(), new Date(yr, 2, 20).getTime()],
  };
  let bestSeasonPct = 0;
  for (const s of Object.keys(SEASON_BOUNDS) as Array<keyof typeof SEASON_BOUNDS>) {
    const [from, to] = SEASON_BOUNDS[s];
    const tot = await db.getFirstAsync<{ n: number }>(
      `SELECT COUNT(*) AS n FROM task
       WHERE property_id = ? AND deleted_at IS NULL
         AND (season = ? OR (due_at >= ? AND due_at < ?))`,
      propertyId, s, from, to
    );
    const dn = await db.getFirstAsync<{ n: number }>(
      `SELECT COUNT(*) AS n FROM task
       WHERE property_id = ? AND deleted_at IS NULL AND completed_at IS NOT NULL
         AND (season = ? OR (due_at >= ? AND due_at < ?))`,
      propertyId, s, from, to
    );
    const t = tot?.n ?? 0;
    const d = dn?.n ?? 0;
    const p = t > 0 ? Math.round((d / t) * 100) : 0;
    if (p > bestSeasonPct) bestSeasonPct = p;
  }

  // Home photo set?
  const hasHomePhoto = !!(prop?.background_uri as string | null | undefined);

  // Completions that carry vendor or cost — rewards "I really did it,
  // here's proof" engagement vs one-tap closes.
  const detailedRow = await db.getFirstAsync<{ n: number }>(
    `SELECT COUNT(*) AS n FROM task_completion tc
     JOIN task t ON t.id = tc.task_id
     WHERE t.property_id = ?
       AND (tc.cost_cents IS NOT NULL OR tc.vendor IS NOT NULL)`,
    propertyId
  );
  const detailedCompletions = detailedRow?.n ?? 0;

  return {
    taskCompletions,
    taskSkips,
    inventoryCount,
    systemCount,
    expenseCount,
    documentCount,
    contactCount,
    profileFilled,
    profileTotal: PROFILE_FIELDS.length,
    goalsCompleted,
    seasonsTouched,
    streakDays,
    bestSeasonPct,
    hasHomePhoto,
    detailedCompletions,
  };
}

function pct(current: number, target: number): number {
  if (target <= 0) return 100;
  return Math.min(100, Math.round((current / target) * 100));
}

export function computeAchievements(stats: AchievementStats): Achievement[] {
  const list: Achievement[] = [
    {
      key: 'first-step',
      title: 'First step',
      body: 'Complete your first task.',
      earned: stats.taskCompletions >= 1,
      progress: pct(stats.taskCompletions, 1),
      tier: 'bronze',
      icon: 'Footprints',
    },
    {
      key: 'on-a-roll',
      title: 'On a roll',
      body: 'Complete 10 tasks.',
      earned: stats.taskCompletions >= 10,
      progress: pct(stats.taskCompletions, 10),
      tier: 'bronze',
      icon: 'Flame',
    },
    {
      key: 'home-historian',
      title: 'Home historian',
      body: 'Complete 50 tasks.',
      earned: stats.taskCompletions >= 50,
      progress: pct(stats.taskCompletions, 50),
      tier: 'silver',
      icon: 'ScrollText',
    },
    {
      key: 'caretaker',
      title: 'Caretaker',
      body: 'Complete 100 tasks.',
      earned: stats.taskCompletions >= 100,
      progress: pct(stats.taskCompletions, 100),
      tier: 'gold',
      icon: 'Heart',
    },
    {
      key: 'four-seasons',
      title: 'Four seasons',
      body: 'Touch a task in every season.',
      earned: stats.seasonsTouched >= 4,
      progress: pct(stats.seasonsTouched, 4),
      tier: 'gold',
      icon: 'CloudSun',
    },
    {
      key: 'mapped',
      title: 'Mapped',
      body: 'Add your address + ZIP so the climate plan tunes properly.',
      earned: stats.profileFilled >= 5,
      progress: pct(stats.profileFilled, 5),
      tier: 'bronze',
      icon: 'MapPin',
    },
    {
      key: 'full-profile',
      title: 'Full profile',
      body: 'Fill in every home detail.',
      earned: stats.profileFilled >= stats.profileTotal,
      progress: pct(stats.profileFilled, stats.profileTotal),
      tier: 'silver',
      icon: 'ClipboardCheck',
    },
    {
      key: 'systems-online',
      title: 'Systems online',
      body: 'Add 5 home systems.',
      earned: stats.systemCount >= 5,
      progress: pct(stats.systemCount, 5),
      tier: 'bronze',
      icon: 'Cpu',
    },
    {
      key: 'inventory-keeper',
      title: 'Inventory keeper',
      body: 'Track 10 inventory items.',
      earned: stats.inventoryCount >= 10,
      progress: pct(stats.inventoryCount, 10),
      tier: 'silver',
      icon: 'PackageOpen',
    },
    {
      key: 'receipts',
      title: 'On the books',
      body: 'Log 10 home expenses.',
      earned: stats.expenseCount >= 10,
      progress: pct(stats.expenseCount, 10),
      tier: 'bronze',
      icon: 'Receipt',
    },
    {
      key: 'binder',
      title: 'Builder of the binder',
      body: 'Upload 10 documents (manuals, warranties, receipts).',
      earned: stats.documentCount >= 10,
      progress: pct(stats.documentCount, 10),
      tier: 'silver',
      icon: 'FolderHeart',
    },
    {
      key: 'network',
      title: 'Little black book',
      body: 'Save 5 pros (plumber, HVAC, electrician, etc).',
      earned: stats.contactCount >= 5,
      progress: pct(stats.contactCount, 5),
      tier: 'bronze',
      icon: 'Users',
    },
    {
      key: 'goal-getter',
      title: 'Goal getter',
      body: 'Complete a project goal.',
      earned: stats.goalsCompleted >= 1,
      progress: pct(stats.goalsCompleted, 1),
      tier: 'silver',
      icon: 'Trophy',
    },
    {
      key: 'streak-3',
      title: 'On the board',
      body: 'Knock something out three days in a row.',
      earned: stats.streakDays >= 3,
      progress: pct(stats.streakDays, 3),
      tier: 'bronze',
      icon: 'Flame',
    },
    {
      key: 'streak-7',
      title: 'Habit forming',
      body: 'Seven-day streak — that’s a routine.',
      earned: stats.streakDays >= 7,
      progress: pct(stats.streakDays, 7),
      tier: 'silver',
      icon: 'Flame',
    },
    {
      key: 'streak-30',
      title: 'Unstoppable',
      body: 'Thirty days in a row. Caretaker of the year.',
      earned: stats.streakDays >= 30,
      progress: pct(stats.streakDays, 30),
      tier: 'gold',
      icon: 'Flame',
    },
    // Volume tiers for individual data types — gives long-tail goals to chase.
    {
      key: 'systems-deep',
      title: 'Wired in',
      body: 'Track 15 home systems.',
      earned: stats.systemCount >= 15,
      progress: pct(stats.systemCount, 15),
      tier: 'silver',
      icon: 'Cpu',
    },
    {
      key: 'inventory-50',
      title: 'Stockroom',
      body: 'Track 50 inventory items.',
      earned: stats.inventoryCount >= 50,
      progress: pct(stats.inventoryCount, 50),
      tier: 'gold',
      icon: 'PackageOpen',
    },
    {
      key: 'expense-50',
      title: 'Cost-conscious',
      body: 'Log 50 home expenses.',
      earned: stats.expenseCount >= 50,
      progress: pct(stats.expenseCount, 50),
      tier: 'silver',
      icon: 'Receipt',
    },
    {
      key: 'expense-100',
      title: 'Bookkeeper',
      body: 'Log 100 home expenses.',
      earned: stats.expenseCount >= 100,
      progress: pct(stats.expenseCount, 100),
      tier: 'gold',
      icon: 'Receipt',
    },
    {
      key: 'docs-25',
      title: 'Paper trail',
      body: 'Upload 25 documents — manuals, warranties, before/after photos.',
      earned: stats.documentCount >= 25,
      progress: pct(stats.documentCount, 25),
      tier: 'gold',
      icon: 'FolderHeart',
    },
    {
      key: 'contacts-10',
      title: 'Rolodex',
      body: 'Save 10 pros — plumber, HVAC, electrician, roofer, landscaper.',
      earned: stats.contactCount >= 10,
      progress: pct(stats.contactCount, 10),
      tier: 'silver',
      icon: 'Users',
    },
    {
      key: 'goal-trio',
      title: 'Project crusher',
      body: 'Complete 3 project goals.',
      earned: stats.goalsCompleted >= 3,
      progress: pct(stats.goalsCompleted, 3),
      tier: 'gold',
      icon: 'Trophy',
    },
    {
      key: 'first-skip',
      title: 'Permission granted',
      body: 'Skip your first task. It’s okay — moving past beats stalling on it.',
      earned: stats.taskSkips >= 1,
      progress: pct(stats.taskSkips, 1),
      tier: 'bronze',
      icon: 'Footprints',
    },
    {
      key: 'season-half',
      title: 'Half-season hero',
      body: 'Complete half the current season’s task list.',
      earned: stats.bestSeasonPct >= 50,
      progress: pct(stats.bestSeasonPct, 50),
      tier: 'silver',
      icon: 'CloudSun',
    },
    {
      key: 'season-full',
      title: 'Season swept',
      body: 'Finish 100% of a season’s tasks. Bragging rights unlocked.',
      earned: stats.bestSeasonPct >= 100,
      progress: pct(stats.bestSeasonPct, 100),
      tier: 'gold',
      icon: 'CloudSun',
    },
    {
      key: 'photo-home',
      title: 'Home portrait',
      body: 'Upload a photo of your home.',
      earned: stats.hasHomePhoto,
      progress: stats.hasHomePhoto ? 100 : 0,
      tier: 'bronze',
      icon: 'Heart',
    },
    {
      key: 'completion-perfect',
      title: 'No questions asked',
      body: 'Log cost or vendor on 10 completions.',
      earned: stats.detailedCompletions >= 10,
      progress: pct(stats.detailedCompletions, 10),
      tier: 'silver',
      icon: 'ClipboardCheck',
    },
  ];

  // Sort: in-progress first (descending by progress), then earned. Keeps
  // the chase visible at the top of the list.
  return list.sort((a, b) => {
    if (a.earned !== b.earned) return a.earned ? 1 : -1;
    return b.progress - a.progress;
  });
}
